import { makeAutoObservable } from "mobx";
import dataRequest from "../http/DataRequest";

class CashShiftsStore {
  shifts = [];
  isLoading = false;
  errorMessage = null;
  filterDiffOnly = false;
  filterWarningsOnly = false;

  constructor() {
    makeAutoObservable(this);
  }

  setFilterDiffOnly(value) {
    this.filterDiffOnly = Boolean(value);
  }

  setFilterWarningsOnly(value) {
    this.filterWarningsOnly = Boolean(value);
  }

  get filteredShifts() {
    let list = this.shifts;
    if (this.filterDiffOnly) {
      list = list.filter((s) => {
        const d = Number(s.cashDiff);
        return !Number.isNaN(d) && d !== 0;
      });
    }
    if (this.filterWarningsOnly) {
      list = list.filter(
        (s) => (s.sessionStatus ?? s.sessionStaus) === "HASWARNINGS",
      );
    }
    return list;
  }

  get summary() {
    const shifts = this.shifts;
    let totalPayOrders = 0;
    let totalCashDiff = 0;
    let diffCount = 0;
    let warningCount = 0;
    for (const s of shifts) {
      const po = Number(s.payOrders);
      if (!Number.isNaN(po)) totalPayOrders += po;
      const cd = Number(s.cashDiff);
      if (!Number.isNaN(cd)) {
        totalCashDiff += cd;
        if (cd !== 0) diffCount += 1;
      }
      if ((s.sessionStatus ?? s.sessionStaus) === "HASWARNINGS") warningCount += 1;
    }
    return {
      totalShifts: shifts.length,
      totalPayOrders,
      totalCashDiff,
      diffCount,
      warningCount,
    };
  }

  get analytics() {
    let salesCash = 0;
    let salesCard = 0;
    let salesCredit = 0;
    let payIn = 0;
    let payOut = 0;
    const byCashier = {};
    const diffs = [];
    for (const s of this.shifts) {
      salesCash += Number(s.salesCash) || 0;
      salesCard += Number(s.salesCard) || 0;
      salesCredit += Number(s.salesCredit ?? s.salesCerdit) || 0;
      payIn += Number(s.payIn) || 0;
      payOut += Number(s.payOut) || 0;
      const name =
        s.responsibleUserName ||
        s.managerName ||
        s.responsibleUser ||
        "—";
      byCashier[name] = (byCashier[name] || 0) + (Number(s.payOrders) || 0);
      const cd = Number(s.cashDiff);
      if (!Number.isNaN(cd) && cd !== 0) {
        diffs.push({ shift: s, cashDiff: cd });
      }
    }
    const cashierRanking = Object.entries(byCashier)
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total);
    diffs.sort((a, b) => Math.abs(b.cashDiff) - Math.abs(a.cashDiff));
    return {
      salesCash,
      salesCard,
      salesCredit,
      payIn,
      payOut,
      cashierRanking,
      diffs,
    };
  }

  setShifts(shifts) {
    this.shifts = shifts;
  }

  setLoading(v) {
    this.isLoading = v;
  }

  setError(msg) {
    this.errorMessage = msg;
  }

  async fetchShifts(serverRowId, openDateFrom, openDateTo, extra = {}) {
    if (serverRowId === "" || serverRowId == null) return;
    this.setLoading(true);
    this.setError(null);
    try {
      const data = await dataRequest.getIikoCashShifts({
        id: serverRowId,
        openDateFrom,
        openDateTo,
        ...extra,
      });
      this.setShifts(data.shifts || []);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Не удалось загрузить кассовые смены";
      this.setError(msg);
      this.setShifts([]);
    } finally {
      this.setLoading(false);
    }
  }
}

const cashShiftsStore = new CashShiftsStore();

export default cashShiftsStore;
